import { Dialog } from "primereact/dialog";
import { useEffect, useState } from "react";
import { Fetch } from "../../shared/Fetch";

interface Props {
  visible: boolean;
  consultantId: number | null;
  consultantName: string;
  toast: any;
  onHide: () => void;
}

interface HistoryItem {
  id: number;
  consultant_ID: number;
  old_Value: boolean;
  new_Value: boolean;
  updated_By: number;
  updated_At: string;
}

function formatDt(str: string) {
  if (!str) return "";
  return new Date(str).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ConsultantMarketingHistory = ({ visible, onHide, toast, consultantId, consultantName }: Props) => {
  const [historyList, setHistoryList] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  const loadHistory = async () => {
    if (!consultantId) return;
    setLoading(true);
    try {
      const response = await Fetch("Consultant_Mast/marketingHistory", "GET", null, `consultantId=${consultantId}`, toast);
      if (response) {
        const data = await response.json();
        setHistoryList(data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && consultantId) loadHistory();
    if (!visible) setHistoryList([]);
  }, [visible, consultantId]);

  return (
    <Dialog visible={visible} header={`Marketing Availability History — ${consultantName}`} style={{ width: "45vw" }} modal onHide={onHide}>
      {loading && (
        <div style={{ textAlign: "center", padding: "2rem", color: "var(--text-color-secondary)" }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: "1.5rem" }} />
        </div>
      )}

      {!loading && !historyList.length && <div style={{ textAlign: "center", padding: "2rem", color: "var(--text-color-secondary)" }}>No history found.</div>}

      {!loading &&
        historyList.map((item, idx) => {
          const isLast = idx === historyList.length - 1;
          const turnedOn = item.new_Value === true;
          const meta = turnedOn ? { icon: "pi-check-circle", color: "#0F6E56", bg: "#E1F5EE", label: "Marked Available" } : { icon: "pi-times-circle", color: "#A32D2D", bg: "#FCEBEB", label: "Marked Unavailable" };

          return (
            <div key={item.id} style={{ display: "flex", gap: "16px" }}>
              {/* Spine */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "32px", flexShrink: 0 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: meta.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1,
                  }}
                >
                  <i className={`pi ${meta.icon}`} style={{ color: meta.color, fontSize: "14px" }} />
                </div>
                {!isLast && <div style={{ width: 2, flex: 1, minHeight: 20, background: "var(--surface-border)", margin: "2px 0" }} />}
              </div>

              <div style={{ flex: 1, paddingBottom: isLast ? 0 : "24px" }}>
                {/* Header row */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span
                    style={{
                      background: meta.bg,
                      color: meta.color,
                      fontSize: 11,
                      fontWeight: 500,
                      padding: "2px 10px",
                      borderRadius: 6,
                    }}
                  >
                    {meta.label}
                  </span>
                  <span style={{ fontSize: 12, color: "var(--text-color-secondary)" }}>{formatDt(item.updated_At)}</span>
                </div>

                <div
                  style={{
                    background: "var(--surface-card)",
                    border: "1px solid var(--surface-border)",
                    borderRadius: 8,
                    padding: "10px 14px",
                  }}
                >
                  {/* Changed by */}
                  <div style={{ fontSize: 12, color: "var(--text-color-secondary)", marginBottom: 8 }}>
                    <i className="pi pi-user" style={{ marginRight: 4 }} />
                    Changed by <span style={{ color: "var(--primary-color)", fontWeight: 600 }}>{item.updated_By}</span>
                  </div>

                  {/* Change row */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                    <span style={{ color: "var(--text-color-secondary)", minWidth: 140, fontSize: 12 }}>Marketing Availability</span>
                    <span
                      style={{
                        color: "var(--text-color)",
                        background: "var(--surface-200)",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        fontSize: 13,
                        fontWeight: 600,
                        letterSpacing: "0.01em",
                      }}
                    >
                      {item.old_Value ? "Available" : "Unavailable"}
                    </span>
                    <span style={{ color: "var(--text-color-secondary)", fontSize: 11 }}>→</span>
                    <span
                      style={{
                        color: item.new_Value ? "#0F6E56" : "#A32D2D",
                        background: item.new_Value ? "#DCFFF6" : "#FFDFDF",
                        padding: "2px 8px",
                        borderRadius: 4,
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {item.new_Value ? "✔ Available" : "✘ Unavailable"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
    </Dialog>
  );
};

export default ConsultantMarketingHistory;
